import { useRef, useState } from 'react';
import { Animated, Text, View } from 'react-native';
import { useTranslation } from 'react-i18next';
import { CuePill, IconButton, TouchLock } from '../index';

import { machineLabel } from './machineLabel';

import { SetCountRow } from './SetCountRow';
import { SupersetTabs } from './SupersetTabs';
import { FocusPanelChipRow } from './FocusPanelChipRow';
import { FocusPanelEffort } from './FocusPanelEffort';
import { FocusPanelRest } from './FocusPanelRest';
import { FocusPanelInputs } from './FocusPanelInputs';
import type { FocusPanelProps } from './FocusPanelProps';
import { makeStyles } from './FocusPanel.styles';
import { FocusPanelDetails } from './FocusPanelDetails';
import { SetTypeChipBar } from './SetTypeChipBar';
import { useAdvanceFlash } from './useAdvanceFlash';
import { useHelpMode } from './useHelpMode';
import { showsPerImplementHint } from './loadingHint';
import { dropsetVariantPatch, removeDropsetPatch } from './dropsetDraft';
import { setNeedsDuration } from './deriveEntryKind';
import { effectivePrescriptionNote } from './prescriptionCue';

// FocusPanel (timeline logger Phase 2) — the active-exercise card + the SINGLE
// data-entry point of the timeline logger. It binds to the active exercise's
// bound set (`set`, the "next set to log") and writes through to the draft on
// every keystroke, so by LOG-SET time the values are already in the flat store
// and the shared cycleSetPill (PR check + rest timer + bodyweight nudge) sees
// fresh data.
//
// Slice 1 ("the plan IS the queue"): the old auto-appended trailing set is gone.
// Sets come from the plan (materialised up front) or the explicit ＋ on the
// SetCountRow. When every set is logged, `set` is null — the card shows the
// row + ＋ but no input, so a workout never carries a phantom trailing set
// (not-done ≠ failed; drop-uncompleted at save still drops any skipped planned
// set). The SetCountRow replaces the old "Set N" subtitle.
export function FocusPanel({
  title,
  entry,
  set,
  activeSetIdx,
  planPerSetNotes,
  planEntryNote,
  canRemove,
  exercise,
  defaultUnit,
  effortScale,
  onChange,
  onSelectSet,
  onAddSet,
  onRemoveSet,
  onToggleUnit,
  onToggleLoadedSides,
  onToggleAmrap,
  timer,
  effortMeter,
  editLocked,
  hideFail = false,
  hideBurnPlus = false,
  hideInert = false,
  prescribeAxis,
  prescribeApplyAll,
  onToggleTimed,
  timedActive = false,
  noteActive = false,
  prescribeNoteEditor,
  onOpenPlates,
  onOpenEffortScale,
  onSwap,
  onSplitGroup,
  onOpenGroupMenu,
  onRemoveExercise,
  historyOpen,
  onToggleHistory,
  onOpenTip,
  groupMembers,
  groupLabel,
  activeEntryId,
  resolveName,
  onSelectMember,
  onCreateGroup,
  cueKey,
  rest,
  restFrozen,
  flashCue,
  targetStrip,
  confirm,
  theme,
}: FocusPanelProps) {
  const { t } = useTranslation();
  const styles = makeStyles(theme);
  const p = theme.palette;

  // Advance-highlight flash (point B) — extracted to useAdvanceFlash.
  const { flashBg, flashBorder } = useAdvanceFlash(cueKey, p);

  // 🔥 Burn+ reveal — the Dropset|Myo-rep selector bar, toggled by the Burn+
  // chip in the qualifier row (the old ⋯ "Set type" menu was retired: it only
  // ever gated this one action; notes + dropset steps render on their own).
  const [burnOpen, setBurnOpen] = useState(false);

  // PE-22 K3.4 — "Nota" editor reveal, same local-UI shape as burnOpen above:
  // the chip's tap only opens/closes this card's own disclosure; the actual
  // text write goes through the injected `prescribeNoteEditor` slot's own
  // onChange, never through this boolean.
  const [noteEditorOpen, setNoteEditorOpen] = useState(false);
  // The ONE close handler, reached from two affordances: the "Nota" chip
  // (re-tapping it toggles the same boolean) and the editor's own explicit
  // close button (review fix, PE-22 K3 triad, Minor a11y) — passed into the
  // `prescribeNoteEditor` render function below.
  const toggleNoteEditor = () => setNoteEditorOpen((v) => !v);

  // Help mode (Burn+ S2, 2026-07-02): the `?` toggle explains the qualifier
  // chips (incl. Dropset/Myo-rep) in place of firing them. One instance per
  // card so a single pinned balloon serves both the chip row and the
  // SetTypeChipBar below it.
  const help = useHelpMode(theme);

  const isBodyweight = entry.isBodyweight;

  // Per-implement entry tip (IB-8 family, Antenore 2026-07-14): the first
  // time per exercise the weight field gains focus on a UNI entry of a
  // handheld lift (two dumbbells / one-arm work), float the existing
  // perImplementHint balloon — «enter ONE implement's weight, the app
  // counts both». Auto-dismissing (useTransientTip), respects the global
  // Hints toggle internally, zero standing UI. Once-per-exercise via a
  // ref so re-focusing the field doesn't nag.
  const perImplementTipSeen = useRef<Set<string>>(new Set());
  const onWeightFocus = () => {
    // Never pre-empt help mode: a pinned chip explanation stays until the
    // user dismisses it ("pinned stays until cleared" — useTransientTip
    // contract); a focus event is not an intent to swap the balloon.
    if (help.helpMode) return;
    if (!exercise || !showsPerImplementHint(exercise)) return;
    if (entry.loadedSides !== 2) return;
    if (perImplementTipSeen.current.has(entry.exerciseId)) return;
    perImplementTipSeen.current.add(entry.exerciseId);
    help.showTip(t('workout.perImplementHint'));
  };

  // Leading action icons for the qualifier row (Antenore 2026-06-01, inbox
  // 107794f1: "Linea history, AMRAP, bi, rir, help") — 🕐 history + the ⓘ
  // help-mode toggle, both via the shared IconButton so they stay identical in
  // size/hitSlop/active treatment. `info` (i), NOT `help` (?) — the ? glyph is
  // the exercise how-to in the controls row; a distinct glyph keeps the two
  // apart at a glance (Antenore 2026-07-02, two-reviewer collision finding).
  // hitSlop is vertical-only: the two icons sit 4px apart in the qualifier
  // row, so symmetric horizontal hitSlop would overlap and mis-route taps
  // (matches the row's Chip convention, 2026-07-02 review).
  const leadingIcons = (
    <>
      <IconButton
        name="clock"
        active={historyOpen}
        onPress={onToggleHistory}
        accessibilityLabel={t('mobile.logger.historyToggle', {
          defaultValue: 'History',
        })}
        hitSlop={{ top: 8, bottom: 8 }}
        theme={theme}
      />
      <IconButton
        name="info"
        active={help.helpMode}
        onPress={help.toggleHelpMode}
        accessibilityLabel={t('mobile.logger.helpToggle', {
          defaultValue: 'Help — tap a chip to learn what it does',
        })}
        hitSlop={{ top: 8, bottom: 8 }}
        theme={theme}
      />
      {/* EQ-1 — which physical machine this entry is on. Shows only when the
          entry carries one, and tapping it REVEALS, never edits: changing
          machine is replace-exercise, the one place the tag is managed
          (equipment-model.md §8.4). It borrows the card's existing balloon
          rather than adding UI of its own, so a tap here still raises the
          single prompt the card is allowed. */}
      {entry.equipmentInstance ? (
        <IconButton
          name="tag"
          // Pinned, like every other explicit "explain this" tap on this card.
          // An unpinned tip obeys the global Hints toggle, so with Hints off
          // the tap would do nothing at all — and it would also replace a
          // pinned help-mode explanation with a self-dismissing one, eating
          // information the athlete asked to keep on screen.
          onPress={() =>
            help.showTip(machineLabel(entry.equipmentInstance!, t), {
              explicit: true,
            })
          }
          accessibilityLabel={machineLabel(entry.equipmentInstance, t)}
          hitSlop={{ top: 8, bottom: 8 }}
          theme={theme}
        />
      ) : null}
    </>
  );
  return (
    <Animated.View
      style={[styles.panel, { backgroundColor: flashBg, borderColor: flashBorder }]}
    >
      {/* AP-3b — in-workout contextual meter at the very top of the card. */}
      {targetStrip}

      {/* The advance cue used to flash here for 1.4s on EVERY log, on a card
          the focus had already left. It moved onto the rest row below (MX-5
          D8), where it stays for the whole countdown. It still appears here for
          the one path with no rest to carry it — a zero-rest lift — because
          otherwise that advance would be signalled by the card's colour pulse
          alone, and colour alone is never the only cue. */}
      {flashCue ? <CuePill theme={theme} label={flashCue} /> : null}

      {/* LG-29 — everything the athlete could edit sits inside this one freeze.
          While a rest counts on this card (restFrozen) it takes no touch and a
          screen reader skips it, but stays fully visible: the set just logged is
          right there to read, it just can't be rewritten until the rest ends
          (tap the timer's ✕ to edit cold). The AMRAP chip (FocusPanelChipRow)
          and the Burn+ bar (SetTypeChipBar) are named suspects of the LG-26 loss
          and are deliberately INSIDE here — the rest row below is the only live
          thing, and it renders outside this wrapper. */}
      <TouchLock locked={restFrozen} style={styles.editBlock}>
      {/* Action bar FIRST — the always-present row: superset affordance (＋ /
          cue) on the left, the exercise action cluster ⇄/❓/🗑 (+ ☰ when
          grouped) on the right. The ⇄/❓/🗑 moved here out of the old title
          row (AP-0g, inbox 7d3e33dc) so the exercise name gets its own line. */}
      <SupersetTabs
        members={groupMembers}
        activeEntryId={activeEntryId}
        groupLabel={groupLabel}
        resolveName={resolveName}
        onSelect={onSelectMember}
        onSplit={onSplitGroup ?? (() => {})}
        onCreate={onCreateGroup}
        onOpenMenu={editLocked ? undefined : onOpenGroupMenu}
        editLocked={editLocked}
        onSwap={onSwap}
        onOpenTip={onOpenTip}
        onRemoveExercise={onRemoveExercise}
        hideInert={hideInert}
        t={t}
        theme={theme}
      />

      {/* Exercise name on its own line (icons moved up into the action bar,
          AP-0g). base + 2-line wrap absorbs the longest DE/RU catalog names. */}
      <Text style={styles.title} numberOfLines={2}>
        {title}
      </Text>

      {/* Qualifier row: 🕐 history + ? help (leading) + BI/UNI, AMRAP, 🔥
          Burn+ chips. The exercise how-to ❓ lives in the title row; the RIR
          chip died with the effort column (AP-0b — the meter is always on). */}
      <FocusPanelChipRow
        entry={entry}
        set={set}
        exercise={exercise}
        onToggleLoadedSides={onToggleLoadedSides}
        onToggleAmrap={onToggleAmrap}
        dropsetActive={(set?.dropset ?? null) !== null}
        onToggleBurnPlus={() => setBurnOpen((v) => !v)}
        burnLocked={editLocked}
        helpMode={help.helpMode}
        showTip={help.showTip}
        leading={leadingIcons}
        hideBurnPlus={hideBurnPlus}
        onToggleTimed={onToggleTimed}
        timedActive={timedActive}
        onToggleNote={prescribeNoteEditor ? toggleNoteEditor : undefined}
        noteActive={noteActive}
        theme={theme}
      />

      {/* K3.4 — the "Nota" editor, revealed by the chip's local open/close
          state, same placement idiom as the Burn+ bar right below it. Its own
          close button fires the SAME toggleNoteEditor the chip uses. */}
      {set && noteEditorOpen && prescribeNoteEditor ? (
        <View style={styles.setTypeBarRow}>
          {prescribeNoteEditor(toggleNoteEditor)}
        </View>
      ) : null}

      {/* Persistent help-mode cue — a faint icon tint alone is easy to miss,
          so while help mode is on the card carries a one-line banner telling
          the user what a chip tap does now + how to leave (2026-07-02 review). */}
      {help.helpMode ? (
        <CuePill
          theme={theme}
          weight="600"
          padY="s2"
          live
          label={t('mobile.logger.helpBanner', {
            defaultValue: 'Help mode — tap a chip to learn what it does; tap ⓘ to exit.',
          })}
        />
      ) : null}

      {/* The single help/lock tip balloon for the whole card — pinned in help
          mode, transient otherwise. Sits right under the chip row (where the ⓘ
          toggle lives) so every chip's tip surfaces in one predictable spot. */}
      {help.tipNode}

      {/* 🔥 Burn+ selector — right UNDER the qualifier row that hosts the Burn+
          chip (Antenore 2026-07-02: "appena sotto la row del Burn+"), not down
          past the inputs. An active chain keeps it visible so × can undo it; a
          past-session edit can't change the type, so it stays hidden there. */}
      {set && !editLocked && (burnOpen || (set.dropset ?? null) !== null) ? (
        <View style={styles.setTypeBarRow}>
          <SetTypeChipBar
            dropset={set.dropset}
            inputsLocked={false}
            onPickVariant={(variant) => {
              const patch = dropsetVariantPatch(set, variant);
              if (patch) onChange(patch);
            }}
            onRemoveChain={() => onChange(removeDropsetPatch(set))}
            onCloseTypeBar={() => setBurnOpen(false)}
            helpMode={help.helpMode}
            showTip={help.showTip}
            confirm={confirm}
            theme={theme}
          />
        </View>
      ) : null}

      {set ? (
        <FocusPanelInputs
          set={set}
          isBodyweight={isBodyweight}
          isCardio={entry.isCardio}
          isTimeBased={entry.isTimeBased}
          defaultUnit={defaultUnit}
          onChange={onChange}
          onOpenPlates={onOpenPlates}
          onToggleUnit={onToggleUnit}
          onWeightFocus={onWeightFocus}
          theme={theme}
        />
      ) : (
        <Text style={styles.allDone}>
          {t('mobile.timeline.allLogged', {
            defaultValue: 'All sets logged — tap + to add another.',
          })}
        </Text>
      )}

      {/* Set-count row AFTER the inputs (Antenore 2026-06-01: "la row con il
          numero di set … va dopo la riga del reps, weight e rir/rpe"). */}
      <SetCountRow
        sets={entry.sets}
        activeSetIdx={activeSetIdx}
        onSelect={onSelectSet}
        onAdd={onAddSet}
        onRemove={onRemoveSet}
        canRemove={canRemove}
        editLocked={editLocked}
        theme={theme}
      />

      <FocusPanelDetails
        set={set}
        prescriptionNote={effectivePrescriptionNote(
          activeSetIdx,
          planPerSetNotes,
        )}
        entryNote={planEntryNote || ''}
        isBodyweight={isBodyweight}
        onChangeSet={onChange}
        editLocked={editLocked}
        theme={theme}
      />
      </TouchLock>

      {/* Effort meter (AP-0b/0c) — the completion gesture of EVERY set kind.
          One full-width easy→hard band; a tap logs the set AND records the
          effort (no escape hatch). Renders only while a set is bound. On a
          duration entry it takes the compact variant and owns the start-timer
          affordance in its slot, so there is no timer row (MX-5 slice 2). */}
      {/* K3.2 — the load-axis selector, in the card's expanded band area:
          right above the meter it decides the shape of. Undefined outside
          prescribe, so nothing renders in log/log-past. */}
      {set && prescribeAxis ? prescribeAxis : null}

      {rest ? (
        <FocusPanelRest
          restTimer={rest.restTimer}
          cueText={rest.cueText}
          onOpenRestPicker={rest.onOpenRestPicker}
          restPickerSaving={rest.restPickerSaving}
          theme={theme}
        />
      ) : set ? (
        <FocusPanelEffort
          set={set}
          setKey={`${entry.exerciseId}#${activeSetIdx ?? -1}`}
          effortScale={effortScale}
          value={effortMeter.value}
          failed={effortMeter.failed}
          onSelect={effortMeter.onSelect}
          onFail={effortMeter.onFail}
          flashRir={effortMeter.flashRir}
          flashKey={effortMeter.flashKey}
          onOpenEffortScale={onOpenEffortScale}
          // ...and NOT kindNeedsDuration(entry): a set the plan bounded in
          // time renders the duration variant of the meter — the clock, the
          // timer column, the wording — on any exercise. The start chip asks
          // the same predicate (timerStartHandlers), so a bout can never be
          // offered a timer the meter has nowhere to draw.
          isDuration={setNeedsDuration(entry, set)}
          isCardio={entry.isCardio}
          timer={timer}
          confirm={confirm}
          hideFail={hideFail}
          theme={theme}
        />
      ) : null}

      {/* K3.3 — "Apply to all sets", BELOW the band (not beside the axis
          selector above it): a broadcast action reads as a consequence of
          the whole prescription just set, meter included. Undefined outside
          prescribe. */}
      {set && prescribeApplyAll ? prescribeApplyAll : null}

      {/* The old exercise-controls row is gone (AP-0d): ✓/✗ became the meter's
          tap; ⇄/❓/🗑 moved up into the action bar (AP-0g) — the meter is the
          card's last word. */}
    </Animated.View>
  );
}
